import PagePanel from "app/components/PagePanel";
import SetupHint from "app/components/AcademySetup/SetupHint";
import {
  isSetupItemOpen,
  showBookingOptionCreatedToast,
} from "app/components/AcademySetup/SetupToast";
import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddPricing from "./Pricing/Modals/AddPricing";
import {
  getFacilityResources,
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import { getResourceCategories } from "app/utils/request/scheduling/resourceCategories";
import EditPricingGroup from "./Pricing/Modals/EditPricingGroup";
import PricingView from "./Pricing/PricingView";
import styles from "./styles.module.scss";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import InfoModal from "../Calendar/InfoModal";
import { RootState } from "app/reducers";
import { useTranslation } from "react-i18next";
import ConflictModal from "./Pricing/Modals/ConflictModal";
import { toast } from "react-toastify";
import { capitalize } from "lodash";
import { useHistory } from "react-router-dom";
import { TimeTable } from "app/routes/Scheduling/Pricing/TimeTableSelector";
import CalendarSettingsLayout from "./Layout";
import { useSelectedCoach } from "app/contexts/SelectedCoachContext";
import CoachSelector from "app/components/CoachSelector";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { Loading } from "app/components/Wrappers";
import { asyncSchedulingProfileGet, setSchedulingData } from "app/actions/user";
import { fetchAcademySetup } from "app/actions/academySetup";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { useActions } from "app/utils/hooks";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { withoutEchoedColor } from "./Pricing/withoutEchoedColor";

interface LessonRate {
  id: string | number;
  rate: number | string;
  defaultRate: boolean;
  location: string;
  capacity: number | string;
  duration: string;
  title: string;
  _destroy?: boolean;
  active: boolean;
  timeTables?: TimeTable[];
  timeTableIds?: number[];
  bookingsLimit?: number | string | null;
  bookingOptionReminder?: string;
  description?: string;
  spaceTemplateId?: string | null;
  // Calendar colour coding; only ever sent by the modals, see below.
  color?: string | null;
  [key: string]: any;
}

interface PricingGroups {
  title: string;
  lessonRates: LessonRate[];
  id: string | number;
}

interface InfoPopupContent {
  title: string;
  status: "error" | "success";
  content: string;
  subcontent?: string;
}

interface ApiError {
  errors?: unknown;
  status?: number;
}

// Two error shapes reach this page. The contract nests the errors of a
// booking option under its list index, such as
// { lesson_rates: { 0: { location: ["must be a string"] } } }. A service error
// is a list of { details: [{ message }] }. A toast needs text, so take the
// first message at any depth. A contract message gets the name of its field
// in front: "Location must be a string".
const firstMessage = (value: unknown, field?: string): string | undefined => {
  if (typeof value === "string") {
    return field ? `${capitalize(field.replace(/_/g, " "))} ${value}` : value;
  }
  if (Array.isArray(value)) {
    return value.map((child) => firstMessage(child, field)).find(Boolean);
  }
  if (value && typeof value === "object") {
    const { message, details } = value as { message?: unknown; details?: unknown };
    if (typeof message === "string") {
      return message;
    }
    if (details) {
      return firstMessage(details);
    }
    for (const [key, child] of Object.entries(value)) {
      const found = firstMessage(child, /^\d+$/.test(key) ? field : key);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

const getErrorMessage = (error: ApiError, fallback: string): string =>
  firstMessage(error?.errors) || fallback;

export default function Pricing() {
  return (
    <CalendarSettingsLayout>
      <PricingContent />
    </CalendarSettingsLayout>
  );
}

function PricingContent() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentFacility = useCurrentFacility();

  // Feature flag for bookings limit field
  const showBookingsLimit =
    currentFacility.data?.featureFlags?.bookingOptionsCapacity === true;

  // Only when the facility has the feature on: the resources tables ship in the
  // same release, and the deployer runs the migration after the deploy.
  const resourceManagementEnabled =
    currentFacility.data?.featureFlags?.resourceManagement === true;
  const [resources, setResources] = useState<ScheduleResourceResponse[]>([]);
  const [categories, setCategories] = useState<ResourceCategoryResponse[]>([]);

  useEffect(() => {
    // Clear first. A switch to a facility without the feature must not keep the
    // last facility's resources, or the picker would send one of them.
    let ignore = false;
    setResources([]);
    setCategories([]);
    const facilityId = currentFacility.data?.id;
    if (!facilityId || !resourceManagementEnabled) {
      return undefined;
    }
    getFacilityResources(facilityId)
      .then((resourcesData) => {
        if (!ignore) {
          setResources(resourcesData);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error("Error fetching resources:", error);
        }
      });
    getResourceCategories(facilityId)
      .then((categoriesData) => {
        if (!ignore) {
          setCategories(categoriesData);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error("Error fetching sections:", error);
        }
      });

    return () => {
      ignore = true;
    };
  }, [currentFacility.data?.id, resourceManagementEnabled]);

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [pricingGroups, setPricingGroups] = useState<PricingGroups>({
    title: "",
    lessonRates: [],
    id: "",
  });
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [infoPopupContent, setInfoPopupContent] = useState<InfoPopupContent>({
    title: "",
    status: "success",
    content: "",
  });
  const { selectedCoachId: contextCoachId } = useSelectedCoach();
  const self = useSelector((state: RootState) => state.user.self);
  // `self` is null until the account loads; the coach-id guards below cover
  // the undefined id.
  const userId = self?.id;
  const academySetupActions = useActions({ fetchAcademySetup });
  // Use context coach ID if available (for admin mode), otherwise fall back to current user
  const selectedCoachId = contextCoachId || userId;

  const [coachProfile, setCoachProfile] = useState(null);

  const [savePricings, setSavePricings] = useState(false);
  const closeEditModal = () => setEditModalOpen(false);
  const openAddModal = () => setAddModalOpen(true);
  const closeAddModal = () => setAddModalOpen(false);

  // Reorder state
  const [isReorderMode, setIsReorderMode] = useState(false);
  // Anchors the academy-setup coachmark to the Add Booking button itself, so
  // pressing it (and only it) dismisses the hint, and only it is described by
  // the hint's text. The button leaves with reorder mode, and the hint with it.
  const addBookingRef = useRef<HTMLButtonElement>(null);
  const [reorderedRates, setReorderedRates] = useState<LessonRate[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [
    isDefaultOptionDeactivatedModalOopen,
    setDefaultOptionDeactivatedModalOopen,
  ] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const history = useHistory();
  const [conflictModalText, setConflictModalText] = useState(
    <div>
      {t("You are deactivating the default booking option.")}
      <br />
      {t(
        "Please select an active booking option as your default before saving changes."
      )}
    </div>
  );

  useEffect(() => {
    if (!selectedCoachId) {
      return;
    }
    const fetchCoachProfile = async () => {
      try {
        const profileData = await schedulingFetchGet(
          `coach_profiles/${selectedCoachId}`
        );
        setCoachProfile(profileData);
      } catch (error) {
        console.error("Error fetching coach profile:", error);
        toast.error("You don't have permission to access this page");
        history.push("/");
      }
    };
    fetchCoachProfile();
  }, [selectedCoachId]);

  useEffect(() => {
    if (!selectedCoachId) {
      return;
    }
    setPricingGroups({ title: "", lessonRates: [], id: "" });
    schedulingFetchGet(`coach_profiles/${selectedCoachId}/pricing_groups`)
      .then((data) => {
        setPricingGroups(data[0] || { title: "", lessonRates: [] });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [savePricings, selectedCoachId]);

  const handleAddPricing = (newPricing: Partial<LessonRate> & Record<string, any>) => {
    newPricing.active = true;
    // Same test the academy checklist uses for booking_options (see
    // AcademySetup/completion.ts): read it before the PATCH so we know
    // whether this add is the coach's first bookable option.
    const hadActiveRate = (pricingGroups.lessonRates || []).some(
      (rate) => rate.active !== false && !rate._destroy,
    );
    // And the checklist's last-fetched flag, as the other rail callers do,
    // so stale local pricing data can't re-celebrate a completed item.
    const bookingOptionsOpen = isSetupItemOpen("booking_options");
    if (!checkDefaultRate(pricingGroups.lessonRates)) {
      newPricing.defaultRate = true;
    }
    if (
      newPricing.defaultRate === true &&
      pricingGroups.lessonRates &&
      pricingGroups.lessonRates.length > 0
    ) {
      pricingGroups.lessonRates.forEach((item) => {
        if (item.defaultRate) {
          item.defaultRate = false;
        }
      });
    }
    const newPricingGroupList = pricingGroups.lessonRates
      ? [...pricingGroups.lessonRates, newPricing]
      : [newPricing];
    schedulingFetchPatch(
      `coach_profiles/${selectedCoachId}/pricing_groups/${pricingGroups.id}`,
      {
        ...pricingGroups,
        lessonRates: newPricingGroupList.map((lessonRate: LessonRate) => {
          return {
            // The rate being added keeps what the modal chose for it.
            ...(lessonRate === newPricing ? lessonRate : withoutEchoedColor(lessonRate)),
            timeTableIds: lessonRate.timeTableIds !== undefined
              ? lessonRate.timeTableIds
              : lessonRate.timeTables?.map((tt) => tt.id),
          };
        }),
      }
    )
      .then(async () => {
        setSavePricings((previous) => !previous);
        asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
        // The checklist tracks the logged-in coach's own setup, so an admin
        // editing another coach's options through CoachSelector keeps the
        // regular confirmation.
        const completesAcademySetupItem =
          !hadActiveRate &&
          bookingOptionsOpen &&
          selectedCoachId === userId &&
          isAcademyOnboardingCoach(self);

        // The designed alert replaces the generic modal — one confirmation
        // per completion, as with Stripe connect. It refreshes the checklist
        // slice and only fires if the API confirms the item, so a save the
        // BE doesn't count still gets the modal.
        const celebrated =
          completesAcademySetupItem &&
          (await showBookingOptionCreatedToast(
            academySetupActions.fetchAcademySetup,
            (path, state) =>
history.push(path, state),
          ));

        if (!celebrated) {
          setInfoModalOpen(true);
          setInfoPopupContent({
            title: t("Changes saved"),
            status: "success",
            content: t("Booking Option added successfully."),
          });
        }
      })
      .catch((error) => {
        console.error("Error updating pricing groups:", error);
        toast.error(getErrorMessage(error, t('Failed to add booking option')));
        setSavePricings((previous) => !previous);
      });
  };

  const checkDefaultRate = (lessonRates: LessonRate[]) => {
    if (lessonRates === undefined || lessonRates === null) {
      return false;
    }
    if (lessonRates.length === 0) {
      return false;
    }
    const count = lessonRates.filter(
      (rate) => rate.defaultRate === true
    ).length;
    if (count === 0) {
      return false;
    }
    return true;
  };

  const handleEditPricing = (updatedItem: LessonRate) => {
    if (!checkDefaultRate(pricingGroups.lessonRates)) {
      updatedItem.defaultRate = true;
    }
    const hasNoDefaultRate =
      pricingGroups.lessonRates
        .filter((rate) => rate.id !== updatedItem.id)
        .filter((rate) => rate.defaultRate === true).length === 0;

    if (updatedItem.defaultRate === false && hasNoDefaultRate) {
      updatedItem.defaultRate = true;
    }

    const lessonRates = pricingGroups.lessonRates.map((item) => {
      const isUpdatedItem = item.id === updatedItem.id;
      const isDefault = isUpdatedItem
        ? updatedItem.defaultRate
        : updatedItem.defaultRate
          ? false
          : item.defaultRate;

      return {
        ...withoutEchoedColor(item),
        ...(isUpdatedItem ? updatedItem : {}),
        timeTableIds: isUpdatedItem && updatedItem.timeTableIds !== undefined
          ? updatedItem.timeTableIds
          : item.timeTables?.map((tt) => tt.id),
        defaultRate: isDefault,
      };
    });

    schedulingFetchPatch(
      `coach_profiles/${selectedCoachId}/pricing_groups/${pricingGroups.id}`,
      {
        ...pricingGroups,
        lessonRates,
      }
    )
      .then(() => {
        setSavePricings((previous) => !previous);
        asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
        setInfoModalOpen(true);
        setInfoPopupContent({
          title: t("Changes saved"),
          status: "success",
          content: t("Booking Option edited successfully."),
        });
      })
      .catch((error) => {
        console.error("Error updating pricing groups:", error);
        toast.error(getErrorMessage(error, t('Failed to save booking option')));
        setSavePricings((previous) => !previous);
      });
  };

  // The removal used to write `_destroy` (and a promoted default) straight onto the objects
  // held in `pricingGroups`. Every handler on this page PATCHes the whole group, so once the
  // API refused the delete — a booking option tied to an offering, or to credit bundles with
  // sessions left — that flag stayed in state and every later add, edit or status change
  // re-sent it. The API refused the delete again each time, which is why the same
  // "Cannot remove a booking option..." error came back on actions that applied correctly.
  // The payload is now derived, and a refusal re-reads the group from the server.
  const handleRemovePricing = (removeItem: LessonRate) => {
    const promotedDefaultId = removeItem.defaultRate
      ? pricingGroups.lessonRates.find(
        (item) => item.id !== removeItem.id && item._destroy !== true
      )?.id
      : undefined;

    schedulingFetchPatch(
      `coach_profiles/${selectedCoachId}/pricing_groups/${pricingGroups.id}`,
      {
        ...pricingGroups,
        lessonRates: pricingGroups.lessonRates.map((item) => {
          const isRemovedItem = item.id === removeItem.id;
          const becomesDefault = item.id === promotedDefaultId;

          return {
            ...withoutEchoedColor(item),
            defaultRate: isRemovedItem ? false : becomesDefault || item.defaultRate,
            _destroy: isRemovedItem ? true : item._destroy,
            timeTableIds: item.timeTables?.map((tt) => tt.id),
          };
        }),
      }
    )
      .then(() => {
        setSavePricings((previous) => !previous);
        asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
        setInfoModalOpen(true);
        setInfoPopupContent({
          title: t("Changes saved"),
          status: "success",
          content: t("Booking Option removed successfully."),
        });
      })
      .catch((error) => {
        console.error("Error updating pricing groups:", error);
        toast.error(getErrorMessage(error, t('Failed to remove booking option')));
        setSavePricings((previous) => !previous);
      });
  };

  const handleEditPricingGroup = (newTitle: string) => {
    setPricingGroups((prevPricingGroups) => ({
      ...prevPricingGroups,
      title: newTitle,
    }));
  };

  const onDismiss = () => {
    setDefaultOptionDeactivatedModalOopen(false);
  };

  const onConfirm = () => {
    if (currentItem !== null) {
      currentItem.active = !currentItem.active;
      saveNewStatus(currentItem);
      setCurrentItem(null);
    }
    setDefaultOptionDeactivatedModalOopen(false);
  };

  // Reorder handlers
  const handleEnterReorderMode = () => {
    const activeRates = pricingGroups.lessonRates.filter((r) => !r._destroy);
    setReorderedRates([...activeRates]);
    setIsReorderMode(true);
  };

  const handleOnDragEnd = useCallback((result: any) => {
    if (!result.destination) { return; }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const items = [...reorderedRates];
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);
    setReorderedRates(items);
  }, [reorderedRates]);

  const handleSaveOrder = useCallback(async () => {
    if (isSaving || !selectedCoachId) { return; }
    setIsSaving(true);

    const lessonRatesMap: Record<string, number> = {};
    reorderedRates.forEach((rate, index) => {
      lessonRatesMap[String(rate.id)] = index + 1;
    });

    try {
      await schedulingFetchPost(
        `coach_profiles/${selectedCoachId}/pricing_groups/sort`,
        { lesson_rates: lessonRatesMap }
      );
      setSavePricings((previous) => !previous);
      setIsReorderMode(false);
      toast.success(t("Order saved successfully"));
    } catch (error) {
      toast.error(t("Failed to save order"));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [reorderedRates, selectedCoachId, isSaving, savePricings, t]);

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedRates([]);
  };

  const handleChangeStatus = (updatedItem: LessonRate) => {
    if (updatedItem !== null) {
      setCurrentItem(updatedItem);
      setConflictModalText(
        <div>
          {t("You deactivated all booking options.")}
          <br />
          {t(
            "To enable athlete bookings, you must have at least one active booking option."
          )}
        </div>
      );
    } else {
      setConflictModalText(
        <div>
          {t("You are deactivating the default booking option.")}
          <br />
          {t(
            "Please select an active booking option as your default before saving changes."
          )}
        </div>
      );
    }
    setDefaultOptionDeactivatedModalOopen(true);
  };

  const saveNewStatus = (updatedItem: LessonRate) => {
    schedulingFetchPatch(
      `coach_profiles/${selectedCoachId}/pricing_groups/${pricingGroups.id}`,
      {
        ...pricingGroups,
        lessonRates: pricingGroups.lessonRates.map((item) => {
          const currentItem = item.id === updatedItem.id ? updatedItem : item;
          return {
            ...withoutEchoedColor(currentItem),
            timeTableIds: currentItem.timeTables?.map((tt) => tt.id),
          };
        }),
      }
    )
      .then((data) => {
        setPricingGroups(data || { title: "", lessonRates: [] });
        asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));

        if (updatedItem.active) {
          toast.success(t("Booking Option activate successfully."));
        } else {
          toast.success(t("Booking Option deactivate successfully."));
        }
      })
      .catch((error) => {
        console.error("Error updating pricing groups:", error);
        toast.error(getErrorMessage(error, t('Failed to update booking option')));
        setSavePricings((previous) => !previous);
      });
  };

  if (!coachProfile) {
    return <Loading isLoading loadType="spinner" />;
  }

  return (
    <>
      <PagePanel
        title={t("Booking Options")}
        headerChildren={
          <div className={styles.page_panel}>
            <ButtonVariant
              title={isReorderMode ? t("Save") : t("Reorder")}
              buttonType={isReorderMode ? "primary" : "secondary-outline"}
              onClickFunc={isReorderMode ? handleSaveOrder : handleEnterReorderMode}
            />
            {isReorderMode && (
              <ButtonVariant
                title={t("Cancel")}
                buttonType="secondary-outline"
                onClickFunc={handleCancelReorder}
              />
            )}
            {!isReorderMode && (
              <>
                <SetupHint
                  itemKey="booking_options"
                  anchorRef={addBookingRef}
                  placement="bottom-end"
                />
                <button
                  type="button"
                  className={styles.button2}
                  onClick={openAddModal}
                  data-testid="add-booking-button"
                  ref={addBookingRef}
                >
                  <span className={styles.fullText}>+ {t("Add Booking")}</span>
                  <span className={styles.shortText}>+ {t("Add")}</span>
                </button>
              </>
            )}
          </div>
        }
      >
        <p className={styles.subtitle}>
          {t(
            "Booking Options represent your coach offerings that can be booked by your athletes."
          )}
        </p>
        <CoachSelector />
        {isReorderMode && (
          <div className={styles.reorder_note}>
            <p className={styles.reorder_note_label}>{t("Note")}:</p>
            <p>
              {t(
                "Reordering changes the display for yourself and athletes when using the booking tools. All public pages will also display in the determined order. Athletes who own a package or subscription will see the related booking option first, then this order will apply."
              )}
            </p>
          </div>
        )}
        {Array.isArray(pricingGroups.lessonRates) &&
        pricingGroups.lessonRates.length > 0 ? (
          isReorderMode ? (
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="coach-booking-options">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {reorderedRates.map((item, index) => (
                      <Draggable
                        key={String(item.id)}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={styles.draggable_item}
                            style={provided.draggableProps.style}
                          >
                            <PricingView
                              list={[item]}
                              onEdit={handleEditPricing}
                              onRemove={handleRemovePricing}
                              titles={pricingGroups.lessonRates.map(
                                (pricingGroup) => pricingGroup.title
                              )}
                              coachCurrencySymbol={coachProfile?.currencySymbol}
                              onChangeStatus={handleChangeStatus}
                              saveNewStatus={saveNewStatus}
                              timeTables={coachProfile?.timeTables || []}
                              showBookingsLimit={showBookingsLimit}
                              stripeIntegrationEnabled={coachProfile?.stripeIntegrationEnabled || currentFacility.data?.stripeIntegrationEnabled}
                              resources={resources}
                              categories={categories}
                              resourcesEnabled={resourceManagementEnabled}
                              isReorderMode
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <PricingView
              list={pricingGroups.lessonRates}
              onEdit={handleEditPricing}
              onRemove={handleRemovePricing}
              titles={pricingGroups.lessonRates.map(
                (pricingGroup) => pricingGroup.title
              )}
              coachCurrencySymbol={coachProfile?.currencySymbol}
              onChangeStatus={handleChangeStatus}
              saveNewStatus={saveNewStatus}
              timeTables={coachProfile?.timeTables || []}
              showBookingsLimit={showBookingsLimit}
              stripeIntegrationEnabled={coachProfile?.stripeIntegrationEnabled || currentFacility.data?.stripeIntegrationEnabled}
              resources={resources}
              categories={categories}
              resourcesEnabled={resourceManagementEnabled}
            />
          )
        ) : (
          <div className={styles.no_bookings}>
            <p>{t("You have not created any booking options yet.")}</p>
            <p>
              {t("You must create booking options to enable athlete booking.")}
            </p>
          </div>
        )}
      </PagePanel>
      <EditPricingGroup
        isOpen={isEditModalOpen}
        title={pricingGroups.title}
        onClose={closeEditModal}
        onSave={handleEditPricingGroup}
      />
      <AddPricing
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onAddPricing={handleAddPricing}
        pricingGroups={
          Array.isArray(pricingGroups.lessonRates) &&
          pricingGroups.lessonRates.map((pricingGroup) => pricingGroup.title)
        }
        coachCurrencySymbol={coachProfile?.currencySymbol}
        timeTables={coachProfile?.timeTables || []}
        showBookingsLimit={showBookingsLimit}
        stripeIntegrationEnabled={coachProfile?.stripeIntegrationEnabled || currentFacility.data?.stripeIntegrationEnabled}
        resources={resources}
        categories={categories}
        resourcesEnabled={resourceManagementEnabled}
      />
      <InfoModal
        isOpen={isInfoModalOpen}
        title={infoPopupContent.title}
        status={infoPopupContent.status}
        onDismiss={() => setInfoModalOpen(false)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>{infoPopupContent.content}</div>
          <div>
            <b> {infoPopupContent.subcontent}</b>
          </div>
        </div>
      </InfoModal>
      <ConflictModal
        isOpen={isDefaultOptionDeactivatedModalOopen}
        title={t('Deactivate Booking')}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {conflictModalText}
        </div>
      </ConflictModal>
    </>
  );
}
